import MobileCalendarScreenModal from "@components/SearchManagement/MobileCalendarScreenModal";
import CalendarToastToolTip from "@components/calendar/CalendarToastToolTip";
import ContinueButton from "@components/common/ContinueButton";
import CalendarIcon from "@components/icons/CalendarIcon";
import ExitButton from "@components/icons/ExitButton";
import LocationIcon from "@components/icons/LocationIcon";
import MediaAssets from "@components/utils/MediaAssets";
import {
    LOCAL_STORAGE,
    RAQ_FORM_TYPES,
} from "@components/utils/constants";
import {
    IAccommodationResponse,
    IActivityResponse,
    ILocationResponse,
    IPackageResponse,
} from "@customTypes/modelResponses";
import useAppContext from "@hooks/useAppContext";
import useRequestQuote, {
    IUseRequestQuoteReturn,
} from "@hooks/useRequestQuote";
import { ITripPreferencesProps } from "@modules/requestAQuote/RaQFullForm";
import RaQForm from "@modules/requestAQuote/components/RaQForm";
import useModalScrollFreeze from "hooks/useModalScrollFreeze";
import Image from "next/legacy/image";
import { RaQFormType } from "pages/things-to-do/[...slug]";
import { FiltersContext } from "providers/FiltersProvider";
import React, { useCallback, useContext, useEffect, useState } from "react";
import ActiveTag from "../ActiveTag";

interface IModalProps extends Pick<IUseRequestQuoteReturn, "onRequestOpen"> {
  onClick: (isOpen: boolean) => void;
  packageData?:
    | IPackageResponse
    | ILocationResponse
    | IAccommodationResponse
    | IActivityResponse;
  raqFormType: RaQFormType;
  tripPreferencesProps: ITripPreferencesProps;
  phone: {
    phoneAction?: string;
    phoneNumber?: string;
  };
}

function isNotLocation(
  obj: any
): obj is IAccommodationResponse | IActivityResponse {
  return "locations" in obj;
}

const Modal = ({
  onRequestOpen,
  onClick,
  packageData,
  raqFormType,
  phone,
  tripPreferencesProps,
}: IModalProps): JSX.Element => {
  const appContext = useAppContext();
  const filtersContext = useContext(FiltersContext);
  const [step, setStep] = useState(1);
  const [calendarError, setCalendarError] = useState(false);
  const [selectedDatesError, setSelectedDatesError] = useState(false);
  const { initializeRequestQuote } = useRequestQuote();

  // Mobile devices (both Android and iOS) give initial value of dateWithNights string of "null"
  useEffect(() => {
    if (
      filtersContext?.calendarUtils.startDateState &&
      filtersContext?.calendarUtils.endDateState
    ) {
      setCalendarError(false);
    }
  }, [
    filtersContext?.calendarUtils.startDateState,
    filtersContext?.calendarUtils.endDateState,
  ]);

  const continueHandler = useCallback(() => {
    if (
      !filtersContext?.calendarUtils.startDateState ||
      !filtersContext?.calendarUtils.endDateState
    ) {
      setCalendarError(true);
      return;
    }
    return (() => {
      initializeRequestQuote(
        tripPreferencesProps.accommodationTags,
        tripPreferencesProps.autoPlacesTags,
        tripPreferencesProps.importantTags,
        packageData
      );
      onClick(true);
    })();
  }, [
    filtersContext?.calendarUtils.startDateState,
    filtersContext?.calendarUtils.endDateState,
    tripPreferencesProps,
  ]);

  const rewriteRAQDates = (): void => {
    const CURRENT_RAQ_STATE =
      // @ts-expect-error
      JSON.parse(localStorage.getItem("USER_REQUEST_QUOTE")) || {};
    CURRENT_RAQ_STATE.startDate =
      filtersContext?.calendarUtils?.startDateState?.format("YYYY-MM-DD");
    CURRENT_RAQ_STATE.endDate =
      filtersContext?.calendarUtils?.endDateState?.format("YYYY-MM-DD");
    localStorage.setItem(
      "USER_REQUEST_QUOTE",
      JSON.stringify(CURRENT_RAQ_STATE)
    );
  };

  const addHandler = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      event.preventDefault();
      if (
        filtersContext?.calendarUtils.startDateState &&
        filtersContext?.calendarUtils.endDateState
      ) {
        localStorage.setItem(
          LOCAL_STORAGE.userSearchQueryParams,
          JSON.stringify({
            startDate:
              filtersContext.calendarUtils.startDateState.format("YYYY-MM-DD"),
            endDate:
              filtersContext.calendarUtils.endDateState.format("YYYY-MM-DD"),
          })
        );
        rewriteRAQDates();
        setSelectedDatesError(false);
      } else {
        setSelectedDatesError(true);
      }
      filtersContext?.calendarUtils.setShowCalendar(false);
    },
    [
      filtersContext?.calendarUtils.startDateState,
      filtersContext?.calendarUtils.endDateState,
    ]
  );

  useModalScrollFreeze([true]);

  const calendarInputProps = {
    setShowCalendar: filtersContext?.calendarUtils.setShowCalendar,
    inputDate: filtersContext?.calendarUtils.inputDate,
    startDate: filtersContext?.calendarUtils.startDateState,
    endDate: filtersContext?.calendarUtils.endDateState,
  };

  return (
    <div className="fixed left-0 top-0 right-0 z-[100] flex h-full w-full flex-col justify-between overflow-auto">
      {raqFormType !== RAQ_FORM_TYPES.FORM_B ? (
        <div className="absolute top-0 z-[60] h-auto w-full overflow-hidden bg-white scrollbar-hide">
          <div className="mx-auto h-full w-[90%]">
            <div className="mt-3 flex items-center justify-between">
              <div>
                <Image
                  src={MediaAssets.getAsset("frog_request_quote")}
                  width="39"
                  height="48"
                  layout="fixed"
                  alt="Request a quote"
                />
              </div>
              <div className="font-medium">Request a Quote</div>
              <div>
                <ExitButton onClick={onRequestOpen} />
              </div>
            </div>
            <div className="mt-3 mb-3 flex items-center justify-center">
              <div className="text-p14 text-midgray">Step 1 of 3</div>
              <div className="mx-3 inline-block h-4 w-[0.2px] bg-blue-20"></div>
              <div className="text-p14 text-midgray">Package Overview</div>
            </div>
            <div className="mt-[1.5rem] flex">
              <h3 className="mb-[6px] font-medium text-darkgray">Dates*</h3>
            </div>

            <form
              className={`flex h-14 w-full rounded-full bg-blue-5 px-[25px] ${
                calendarError ? "error-field" : ""
              }`}
            >
              <div
                className="flex w-full items-center"
                onClick={() => {
                  filtersContext?.calendarUtils.setShowCalendar(true);
                }}
              >
                <CalendarIcon />
                <input
                  type="text"
                  className="ml-[11px] flex-grow bg-blue-5 text-black"
                  id="datepicker"
                  autoComplete="off"
                  readOnly
                  value={filtersContext?.calendarUtils.inputDate as string}
                  placeholder={"Add dates"}
                />
              </div>
            </form>
            {calendarError && (
              <span className="error-text">{"Dates field is required"}</span>
            )}

            <div className="mt-8 flex">
              <h3 className="mb-[6px] font-medium text-darkgray">
                Places You&apos;ll Visit
              </h3>
            </div>
            {packageData && (
              <div className="flex gap-4">
                {isNotLocation(packageData) && packageData.locations ? (
                  packageData.locations.map((location) => {
                    return (
                      <div
                        key={location.id}
                        className="flex items-center gap-2"
                      >
                        <div className="h-[0.813rem] w-[0.625rem] stroke-darkgray">
                          <LocationIcon />
                        </div>
                        <div className="text-darkgray">{location.Title}</div>
                      </div>
                    );
                  })
                ) : (
                  <div className="flex gap-2">
                    <div className="h-[0.813rem] w-[0.625rem] stroke-darkgray">
                      <LocationIcon />
                    </div>
                    <div className="text-darkgray">{packageData?.Title}</div>
                  </div>
                )}
              </div>
            )}
            <div className="mt-8 flex">
              <h3 className="mb-[6px] font-medium text-darkgray">
                Where To Stay
              </h3>
            </div>

            <div className="flex flex-wrap">
              {tripPreferencesProps.accommodationTags.length > 0 &&
                tripPreferencesProps.accommodationTags.map((row) => (
                  <ActiveTag
                    key={`${row.id}${row.title}`}
                    tagData={row}
                    onClick={tripPreferencesProps.handleRemoveTag as () => void}
                  />
                ))}
            </div>

            <div className="mt-8 flex">
              <h3 className="mb-[6px] font-medium text-darkgray">
                Things To Do
              </h3>
            </div>
            <div className="flex flex-wrap">
              {tripPreferencesProps.autoPlacesTags.length > 0 &&
                tripPreferencesProps.autoPlacesTags.map((row) => (
                  <ActiveTag
                    key={`${row.id}${row.title}`}
                    tagData={row}
                    onClick={tripPreferencesProps.handleRemoveTag as () => void}
                  />
                ))}
            </div>
            <div className="mt-8 flex">
              <h3 className="mb-[6px] font-medium text-darkgray">Important</h3>
            </div>
            <div className="mb-[8rem] flex flex-wrap">
              {tripPreferencesProps.importantTags.length > 0 &&
                tripPreferencesProps.importantTags.map((tag) => (
                  <ActiveTag
                    key={`${tag.id}${tag.title}`}
                    tagData={tag}
                    onClick={tripPreferencesProps.handleRemoveTag as () => void}
                  />
                ))}
            </div>
          </div>
          <ContinueButton onClick={continueHandler} />
        </div>
      ) : (
        <>
          <RaQForm
            closeRaQ={onRequestOpen}
            isMobile={appContext?.isMobile}
            calendarInputProps={calendarInputProps}
            step={step}
            setStep={setStep}
            tripPreferencesProps={tripPreferencesProps}
            phone={phone}
            dates={{
              startDate: calendarInputProps.startDate,
              endDate: calendarInputProps.endDate,
            }}
          />
        </>
      )}
      {filtersContext?.calendarUtils.showCalendar && (
        <div className="fixed left-0 top-0 right-0 z-[100] flex h-full w-full flex-col justify-between overflow-auto bg-white">
          <MobileCalendarScreenModal
            title={"When is your trip?"}
            backHandler={() => {
              filtersContext?.calendarUtils.resetToLastValues();
              filtersContext?.calendarUtils.setShowCalendar(false);
            }}
            calendarUtils={filtersContext?.calendarUtils}
            secondaryButtonHandler={() => {
              filtersContext?.calendarUtils.resetValues();
            }}
            primaryButtonHandler={addHandler}
          />
          {selectedDatesError && (
            <div className="fixed bottom-0 z-[110] w-full px-5">
              <CalendarToastToolTip
                setSelectedDatesError={setSelectedDatesError}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Modal;
